/**
 * @file Tabs.tsx
 * @description Вкладки разделов страницы (phase19.31, шаг О4; SC45).
 *
 * Вкладка меняет РАЗДЕЛ страницы («Очередь / Журнал», «Остатки / Движения»)
 * и не должна выглядеть как кнопка действия: текст 14/500 с линией снизу у
 * выбранной, высота — как у кнопки (`--control-h`: 48 до 1024, 44 с 1024,
 * 56 у кладовщика до 1024). Счётчик — серой меткой справа от подписи.
 * Фильтры списка — другой компонент (`FilterChips`).
 *
 * На телефоне ряд прокручивается горизонтально с затуханием края, а не
 * сжимается; `shrink-0` обязателен — в колонке с прокруткой ряд иначе
 * схлопывается до нуля (грабли 360px). Вертикальной прокрутки у ряда нет
 * (`overflow-y-hidden`, без отрицательных отступов у вкладки): иначе
 * `overflow-x-auto` превращал переполнение в 1px в полосу со стрелками
 * ▲▼ справа (дефект карточки заказа, phase19.31).
 *
 * Метка-точка (`marker`) — «здесь нужно ваше решение» (вкладка «Доставка»
 * заказа): цветом действия, с подписью для программ чтения экрана.
 *
 * Клавиатура — по шаблону WAI-ARIA tabs: стрелки, Home/End.
 *
 * Выбранная вкладка всегда видна: если она за краем прокручиваемого ряда
 * (узкая панель конструктора, вкладку выбрали по адресу), ряд сдвигается к
 * ней — только свой `scrollLeft`, без `scrollIntoView`, чтобы не двигать
 * страницу (урок OrderChainStepper; замечание владельца 2026-10-11).
 *
 * ```tsx
 * <Tabs
 *   aria-label="Режим списка заданий"
 *   items={[{ value: "queue", label: "Очередь", count: 3 }, { value: "history", label: "Журнал" }]}
 *   value={view}
 *   onChange={setView}
 * />
 * ```
 */

import type { KeyboardEvent } from "react";

import { useRevealActive } from "./useRevealActive";
import { useScrollFade } from "./useScrollFade";
import { cn } from "@/lib/utils";

/** Описание вкладки. Значение — строка-дискриминант. */
export interface TabItem<T extends string> {
  value: T;
  label: string;
  /** Счётчик справа от подписи; `undefined` — без счётчика. */
  count?: number;
  /** Точка «требует решения»: текст — подпись точки для скринридера. */
  marker?: string;
}

interface Props<T extends string> {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  "aria-label": string;
  className?: string;
}

/** Сдвиг выбора стрелками: по кругу, Home/End — к краям. */
const KEY_STEP: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (i, n) => (i + 1) % n,
  ArrowLeft: (i, n) => (i - 1 + n) % n,
  Home: () => 0,
  End: (_i, n) => n - 1,
};

export function Tabs<T extends string>({ items, value, onChange, className, "aria-label": ariaLabel }: Props<T>) {
  const fade = useScrollFade<HTMLDivElement>();
  const { ref: rowRef, update: updateFade } = fade;
  const activeIndex = items.findIndex((item) => item.value === value);

  // Выбранная вкладка за краем ряда — сдвигаем ряд к ней (общий хук с оглавлением групп настроек).
  useRevealActive(rowRef, activeIndex, "[role='tab']", updateFade);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = KEY_STEP[event.key];
    if (!step || items.length === 0) return;
    event.preventDefault();
    const current = Math.max(0, items.findIndex((item) => item.value === value));
    const next = items[step(current, items.length)];
    if (!next) return;
    onChange(next.value);
    // Фокус — за выбором: кнопки в DOM в том же порядке, что и items.
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>("[role='tab']");
    buttons[items.indexOf(next)]?.focus();
  }

  return (
    <div
      ref={fade.ref}
      onScroll={fade.update}
      style={fade.style}
      role="tablist"
      aria-label={ariaLabel}
      data-slot="tabs"
      onKeyDown={onKeyDown}
      className={cn(
        "flex shrink-0 items-stretch gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none]",
        "[box-shadow:inset_0_-1px_0_var(--border)]",
        className,
      )}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.value)}
            className={cn(
              "relative inline-flex min-h-[var(--control-h,3rem)] shrink-0 items-center gap-2 px-3",
              "whitespace-nowrap text-[length:var(--fs-control,0.875rem)] font-medium outline-none transition-colors",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
              // Выбранная — цвет действия и линия 2px снизу поверх общей линии ряда.
              active
                ? "text-primary after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary"
                : "text-on-surface-variant hover:text-on-surface",
            )}
          >
            {item.label}
            {item.count !== undefined ? (
              <span className="type-caption rounded-full bg-surface-low px-1.5 tabular-nums text-on-surface-variant">
                {item.count}
              </span>
            ) : null}
            {item.marker ? (
              <span role="img" aria-label={item.marker} className="inline-block size-2 shrink-0 rounded-full bg-primary" />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
